import {
  AttributeOptions,
  FNOL_RequestForm,
  MultiSelectOption,
} from '@dispatch/shared/types';
import { Section, SelectField, TextField } from '@dispatch/web/ui';
import { Close } from '@mui/icons-material';
import { Divider, Grid, IconButton } from '@mui/material';
import { get } from 'lodash';
import { useMemo } from 'react';
import {
  Control,
  FieldValues,
  UseFieldArrayRemove,
  UseFormGetValues,
  useFormState,
} from 'react-hook-form';

const PINNED_DISPLAY_NAME_INPUT_PROPS = { readOnly: true };

export type FNOLClaimAttributesFormProps = {
  control: Control<FieldValues>;
  claimAttributeIndex: number;
  claimAttributeRemove: UseFieldArrayRemove;
  field: Record<'id', string>;
  getValues: UseFormGetValues<FNOL_RequestForm>;
  companyAttributes: AttributeOptions[];
  /** A pinned row is required and cannot be removed. */
  isPinned?: boolean;
};

export function FNOLClaimAttributesForm({
  control,
  claimAttributeIndex,
  claimAttributeRemove,
  field,
  getValues,
  companyAttributes,
  isPinned = false,
}: FNOLClaimAttributesFormProps) {
  const { companyAttribute, isMultiSelect, options } = useMemo(() => {
    const companyAttribute = companyAttributes.find(
      (attribute) =>
        getValues(
          `claimAttributes.${claimAttributeIndex}.attributeDefinitionIntegrationGUID`
        ) === attribute.value
    );

    const isMultiSelect = companyAttribute?.valueType?.includes('multi-select');

    const options =
      companyAttribute?.attributeDefinitionValues.map((value) => {
        return {
          title: value.title,
          value: value.value,
        };
      }) || [];

    return {
      companyAttribute,
      isMultiSelect,
      options,
    };
  }, [claimAttributeIndex, companyAttributes, getValues]);

  const valuesFieldName = `claimAttributes.${claimAttributeIndex}.selectedAttributeValuesVarchars`;
  const { errors } = useFormState({ control, name: valuesFieldName });
  const valuesError = get(errors, valuesFieldName);
  const valuesErrors = valuesError
    ? { [valuesFieldName]: valuesError }
    : undefined;

  const deleteAttribute = (index: number) => {
    claimAttributeRemove(index);
  };

  const displayNameInputProps = isPinned
    ? PINNED_DISPLAY_NAME_INPUT_PROPS
    : {
        readOnly: true,
        endAdornment: (
          <IconButton onClick={() => deleteAttribute(claimAttributeIndex)}>
            <Close />
          </IconButton>
        ),
      };

  const renderClaimAttributes = () => {
    return (
      <>
        <div
          style={{
            padding: '24px',
          }}
        >
          <Grid container rowSpacing={4} key={field.id}>
            <Grid item xs={6}>
              <TextField
                id={`claimAttributes.${claimAttributeIndex}.displayName`}
                name={`claimAttributes.${claimAttributeIndex}.displayName`}
                label="Attribute"
                type="text"
                control={control}
                inputProps={displayNameInputProps}
              />
            </Grid>
            <Grid item xs={10}>
              <SelectField
                multiple={isMultiSelect}
                id={valuesFieldName}
                name={valuesFieldName}
                label={companyAttribute?.title}
                required={isPinned}
                errors={valuesErrors}
                control={control}
                options={options as MultiSelectOption[]}
                isOptionEqualToValue={(
                  option: MultiSelectOption,
                  selectValue: any
                ) => {
                  return (
                    option.value === selectValue?.value ||
                    option.value === selectValue
                  );
                }}
              />
            </Grid>
          </Grid>
        </div>
        <Divider />
      </>
    );
  };

  return <Section>{renderClaimAttributes()}</Section>;
}
